import { useEffect, useState } from 'react';

// Threshold at which the compact mode activates when scrolling down
const SCROLL_COMPACT_THRESHOLD = 80;

/**
 * Custom hook for scroll detection with hysteresis to prevent flickering.
 *
 * When scrolling down, switches to compact mode at the specified threshold.
 * When scrolling up, only returns to normal mode when reaching the exact top (0px).
 * This prevents rapid state toggling near the top of the page.
 *
 * @param threshold - The scroll position (in pixels) at which to activate compact mode (default: 80)
 * @returns Boolean indicating whether the page is scrolled past the threshold
 */
export function useScrollWithHysteresis(threshold: number = SCROLL_COMPACT_THRESHOLD): boolean {
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        // Check initial scroll position on mount
        const initialScrollY = window.scrollY;
        setIsScrolled(initialScrollY > threshold);

        const handleScroll = () => {
            const scrollY = window.scrollY;

            // When scrolling down, switch to compact at threshold
            // When scrolling up, switch back to normal only at 0px (top of page)
            // This prevents any flickering near the top
            setIsScrolled((prevIsScrolled) => {
                if (scrollY > threshold && !prevIsScrolled) {
                    return true;
                } else if (scrollY === 0 && prevIsScrolled) {
                    return false;
                }
                return prevIsScrolled;
            });
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [threshold]);

    return isScrolled;
}